import { c as _c } from "react/compiler-runtime";
import { getEntitiesByFpIdLabelOptions } from '@onefootprint/fetch-dashboard';
import { IcoArrowUpRight16, IcoBook16, IcoBuilding16, IcoChevronLeft16, IcoChevronRight16, IcoCopy16, IcoDotsHorizontal16, IcoFlag16, IcoMessage16, IcoRobot16, IcoTag16, IcoUser16 } from '@onefootprint/icons';
import type { Investigation } from '@onefootprint/request-types/dashboard';
import { Button, Dropdown, IconButton, Tooltip } from '@onefootprint/ui';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FraudLabelMenuItems } from '@/components/fraud-label';
import { IdMenuItems } from '@/components/id-dropdown';
import ManualReview from '@/components/manual-review';
import PermissionGate from '@/components/permission-gate';
import { agentBuilderUrl } from '@/config/constants';
import useEntityId from '@/entity/hooks/use-entity-id';
import usePermissions from '@/hooks/use-permissions';
import useInvestigationIdItems from '../../hooks/use-investigation-id-items';
import InvestigationChatDialog from './investigation-chat-dialog';
import { StatusesDialog, StatusesPopover, useCustomStatuses } from './statuses-popover';
import type { HeaderProps } from './types';
const openInNewTab = (path: string) => window.open(path, '_blank', 'noopener,noreferrer');
const getAgentPath = (agent: NonNullable<Investigation['agent']>) => agentBuilderUrl(agent.id, {
  versionId: agent.versionId
});
const useSubjectLink = investigation => {
  const $ = _c(8);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "header.actions"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("investigations", t0);
  const isBusiness = investigation.entityKind === "business";
  const t1 = isBusiness ? `/businesses/${investigation.fpId}` : `/users/${investigation.fpId}`;
  let t2;
  if ($[1] !== isBusiness || $[2] !== t) {
    t2 = isBusiness ? t(_temp) : t(_temp2);
    $[1] = isBusiness;
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = isBusiness ? IcoBuilding16 : IcoUser16;
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      path: t1,
      label: t2,
      icon: t3
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
const useGoToAgentLabel = agent => {
  const $ = _c(5);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "header.agents"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("investigations", t0);
  if (!agent) {
    return;
  }
  const {
    name,
    versionTag
  } = agent;
  let t1;
  if ($[1] !== name || $[2] !== t || $[3] !== versionTag) {
    t1 = versionTag ? t(_temp3, {
      name,
      version: versionTag
    }) : t(_temp4, {
      name
    });
    $[1] = name;
    $[2] = t;
    $[3] = versionTag;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};

// The top-bar's action group: investigation-level only — Review user, Statuses
// popover, a go-to-subject button, and the Agents menu. Per-document controls live in
// the ContentHeader; sub-investigations live in the centered title dropdown.
const ActionButtons = props => {
  const $ = _c(16);
  const hasEntityActions = !!props.entity;
  const hasSources = !!props.onOpenSources && !!props.sourceCount;
  const hasPrecedingActions = hasEntityActions || !!props.investigation.fpId || hasSources;
  let t0;
  if ($[0] !== props.entity) {
    t0 = props.entity && <><PermissionGate scopeKind="manual_review" fallbackText=""><ManualReview status={props.entity.status} kind={props.entity.kind} /></PermissionGate><StatusesPopover entity={props.entity} /></>;
    $[0] = props.entity;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== hasEntityActions || $[3] !== props.investigation) {
    t1 = props.investigation.fpId && <GoToSubjectButton investigation={props.investigation} iconOnly={hasEntityActions} />;
    $[2] = hasEntityActions;
    $[3] = props.investigation;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== hasSources || $[6] !== props) {
    t2 = hasSources && <SourcesButton {...props} />;
    $[5] = hasSources;
    $[6] = props;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== hasPrecedingActions || $[9] !== props.investigation) {
    t3 = <AgentMenu investigation={props.investigation} hasPrecedingActions={hasPrecedingActions} />;
    $[8] = hasPrecedingActions;
    $[9] = props.investigation;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t0 || $[12] !== t1 || $[13] !== t2 || $[14] !== t3) {
    t4 = <>{t0}{t1}{t2}{t3}</>;
    $[11] = t0;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
};
const AgentMenu = t0 => {
  const $ = _c(35);
  const {
    investigation,
    hasPrecedingActions
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "header.agents"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("investigations", t1);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const {
    agent,
    fpId,
    investigationId
  } = investigation;
  const goToAgentLabel = useGoToAgentLabel(agent);
  const showTranscript = !!fpId;
  const showGoToAgent = !!agent;
  if (!showTranscript && !showGoToAgent) {
    return null;
  }
  let t2;
  if ($[1] !== hasPrecedingActions) {
    t2 = hasPrecedingActions && <span aria-hidden="true" className="w-px h-5 bg-(--border-color-tertiary)" />;
    $[1] = hasPrecedingActions;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t) {
    t3 = t(_temp5);
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t(_temp6);
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <Dropdown.Trigger asChild={true}><IconButton aria-label={t4} icon={IcoRobot16} size="compact" variant="outline" /></Dropdown.Trigger>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== menuOpen || $[10] !== t3 || $[11] !== t5) {
    t6 = <Tooltip content={t3} disabled={menuOpen}>{t5}</Tooltip>;
    $[9] = menuOpen;
    $[10] = t3;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== showTranscript || $[14] !== t) {
    t7 = showTranscript && <Dropdown.Item size="compact" iconLeft={IcoMessage16} onSelect={() => setTranscriptOpen(true)}>{t(_temp7)}</Dropdown.Item>;
    $[13] = showTranscript;
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== agent || $[17] !== goToAgentLabel) {
    t8 = agent && <Dropdown.Item size="compact" iconLeft={IcoArrowUpRight16} onSelect={() => openInNewTab(getAgentPath(agent))}>{goToAgentLabel}</Dropdown.Item>;
    $[16] = agent;
    $[17] = goToAgentLabel;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t7 || $[20] !== t8) {
    t9 = <Dropdown.Portal><Dropdown.Content align="end" className="min-w-[220px]"><Dropdown.Group>{t7}{t8}</Dropdown.Group></Dropdown.Content></Dropdown.Portal>;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== menuOpen || $[23] !== t6 || $[24] !== t9) {
    t10 = <Dropdown.Root open={menuOpen} onOpenChange={setMenuOpen}>{t6}{t9}</Dropdown.Root>;
    $[22] = menuOpen;
    $[23] = t6;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== fpId || $[27] !== investigationId || $[28] !== showTranscript || $[29] !== transcriptOpen) {
    t11 = showTranscript && fpId && <InvestigationChatDialog open={transcriptOpen} onClose={() => setTranscriptOpen(false)} fpId={fpId} investigationId={investigationId} />;
    $[26] = fpId;
    $[27] = investigationId;
    $[28] = showTranscript;
    $[29] = transcriptOpen;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t10 || $[32] !== t11 || $[33] !== t2) {
    t12 = <>{t2}{t10}{t11}</>;
    $[31] = t10;
    $[32] = t11;
    $[33] = t2;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  return t12;
};
const SourcesButton = t0 => {
  const $ = _c(14);
  const {
    citedSourceCount: t1,
    onOpenSources,
    sourceCount: t2
  } = t0;
  const citedSourceCount = t1 === undefined ? 0 : t1;
  const sourceCount = t2 === undefined ? 0 : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      keyPrefix: "header.actions"
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const {
    t
  } = useTranslation("investigations", t3);
  let t4;
  if ($[1] !== sourceCount || $[2] !== t) {
    t4 = t(_temp8, {
      count: sourceCount
    });
    $[1] = sourceCount;
    $[2] = t;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== citedSourceCount || $[5] !== sourceCount || $[6] !== t) {
    t5 = t(_temp9, {
      cited: citedSourceCount,
      total: sourceCount
    });
    $[4] = citedSourceCount;
    $[5] = sourceCount;
    $[6] = t;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== onOpenSources || $[9] !== t5) {
    t6 = <IconButton aria-label={t5} icon={IcoBook16} size="compact" variant="outline" onClick={onOpenSources} />;
    $[8] = onOpenSources;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t4 || $[12] !== t6) {
    t7 = <Tooltip content={t4}>{t6}</Tooltip>;
    $[11] = t4;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
};
const GoToSubjectButton = t0 => {
  const $ = _c(13);
  const {
    investigation,
    iconOnly
  } = t0;
  const {
    path,
    label,
    icon
  } = useSubjectLink(investigation);
  let t1;
  if ($[0] !== path) {
    t1 = () => openInNewTab(path);
    $[0] = path;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const open = t1;
  if (iconOnly) {
    let t2;
    if ($[2] !== icon || $[3] !== label || $[4] !== open) {
      t2 = <IconButton aria-label={label} icon={icon} size="compact" variant="outline" onClick={open} />;
      $[2] = icon;
      $[3] = label;
      $[4] = open;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    let t3;
    if ($[6] !== label || $[7] !== t2) {
      t3 = <Tooltip content={label}>{t2}</Tooltip>;
      $[6] = label;
      $[7] = t2;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    return t3;
  }
  let t2;
  if ($[9] !== icon || $[10] !== label || $[11] !== open) {
    t2 = <Button size="compact" variant="secondary" prefixIcon={icon} onClick={open}>{label}</Button>;
    $[9] = icon;
    $[10] = label;
    $[11] = open;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return t2;
};

// A set label shows as its own chip beside the status, so the menu only offers to add one.
const useCanAddFraudLabel = hasEntity => {
  const $ = _c(8);
  const entityId = useEntityId();
  const {
    hasPermission
  } = usePermissions();
  let t0;
  if ($[0] !== entityId) {
    t0 = getEntitiesByFpIdLabelOptions({
      path: {
        fpId: entityId
      }
    });
    $[0] = entityId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = hasEntity && !!entityId;
  let t2;
  if ($[2] !== t0 || $[3] !== t1) {
    t2 = {
      ...t0,
      enabled: t1
    };
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data: label
  } = useQuery(t2);
  let t3;
  if ($[5] !== hasPermission || $[6] !== label) {
    t3 = !!label && !label.kind && hasPermission("label_and_tag");
    $[5] = hasPermission;
    $[6] = label;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
type MenuView = 'actions' | 'fraudLabel' | 'ids';

// Phones fold every action above except Review into one `…` menu, with the IDs and fraud label the
// desktop shows as chips; those open their options in place, since a phone has no room for a submenu.
// The dialogs sit outside Dropdown.Content, which unmounts on close.
const ActionsMenu = t0 => {
  const $ = _c(100);
  const {
    investigation,
    entity,
    onOpenSources,
    sourceCount: t1,
    defaultOpen,
    defaultView: t2
  } = t0;
  const sourceCount = t1 === undefined ? 0 : t1;
  const defaultView = t2 === undefined ? "actions" : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      keyPrefix: "header"
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const {
    t
  } = useTranslation("investigations", t3);
  const {
    t: tCommon
  } = useTranslation("common");
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      keyPrefix: "headerDefault.fraudLabel"
    };
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const {
    t: tFraudLabel
  } = useTranslation("entity-details", t4);
  const [openDialog, setOpenDialog] = useState(null);
  const [view, setView] = useState(defaultView);
  const backRef = useRef(null);
  const fraudLabelItemRef = useRef(null);
  const idsItemRef = useRef(null);
  const customStatuses = useCustomStatuses(entity?.kind);
  const canAddFraudLabel = useCanAddFraudLabel(!!entity);
  const idItems = useInvestigationIdItems(investigation, entity);
  const subject = useSubjectLink(investigation);
  const {
    agent,
    fpId,
    investigationId
  } = investigation;
  const goToAgentLabel = useGoToAgentLabel(agent);
  const showStatuses = !!entity && customStatuses.length > 0;
  const showSources = !!onOpenSources && sourceCount > 0;
  const hasAgentItems = !!fpId || !!agent;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setOpenDialog(null);
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const closeDialog = t5;
  let t6;
  if ($[3] !== t || $[4] !== tFraudLabel || $[5] !== view) {
    t6 = view === "fraudLabel" ? tFraudLabel(_temp10) : t(_temp11);
    $[3] = t;
    $[4] = tFraudLabel;
    $[5] = view;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const viewTitle = t6;
  let T0;
  let T1;
  let T2;
  let t10;
  let t11;
  let t12;
  let t13;
  let t7;
  let t8;
  let t9;
  if ($[7] !== agent || $[8] !== canAddFraudLabel || $[9] !== defaultOpen || $[10] !== fpId || $[11] !== goToAgentLabel || $[12] !== hasAgentItems || $[13] !== idItems || $[14] !== onOpenSources || $[15] !== showSources || $[16] !== showStatuses || $[17] !== sourceCount || $[18] !== subject || $[19] !== t || $[20] !== tCommon || $[21] !== tFraudLabel || $[22] !== view || $[23] !== viewTitle) {
    const showView = next => event => {
      event.preventDefault();
      const openedFrom = view === "fraudLabel" ? fraudLabelItemRef : idsItemRef;
      flushSync(() => setView(next));
      (next === "actions" ? openedFrom : backRef).current?.focus();
    };
    let t14;
    if ($[34] !== showStatuses || $[35] !== t) {
      t14 = showStatuses && <Dropdown.Item iconLeft={IcoTag16} onSelect={() => setOpenDialog("statuses")} className="h-10">{t(_temp12)}</Dropdown.Item>;
      $[34] = showStatuses;
      $[35] = t;
      $[36] = t14;
    } else {
      t14 = $[36];
    }
    const t15 = canAddFraudLabel && <Dropdown.Item ref={fraudLabelItemRef} iconLeft={IcoFlag16} iconRight={IcoChevronRight16} onSelect={showView("fraudLabel")} className="h-10">{tFraudLabel(_temp13)}</Dropdown.Item>;
    const t16 = showView("ids");
    let t17;
    if ($[37] !== t) {
      t17 = t(_temp14);
      $[37] = t;
      $[38] = t17;
    } else {
      t17 = $[38];
    }
    const t18 = <Dropdown.Item ref={idsItemRef} iconLeft={IcoCopy16} iconRight={IcoChevronRight16} onSelect={t16} className="h-10">{t17}</Dropdown.Item>;
    let t19;
    if ($[39] !== fpId || $[40] !== subject) {
      t19 = fpId && <Dropdown.Item iconLeft={subject.icon} asLink={true} onSelect={() => openInNewTab(subject.path)} className="h-10">{subject.label}</Dropdown.Item>;
      $[39] = fpId;
      $[40] = subject;
      $[41] = t19;
    } else {
      t19 = $[41];
    }
    let t20;
    if ($[42] !== onOpenSources || $[43] !== showSources || $[44] !== sourceCount || $[45] !== t) {
      t20 = showSources && <Dropdown.Item iconLeft={IcoBook16} onSelect={onOpenSources} className="h-10">{t(_temp15, {
          count: sourceCount
        })}</Dropdown.Item>;
      $[42] = onOpenSources;
      $[43] = showSources;
      $[44] = sourceCount;
      $[45] = t;
      $[46] = t20;
    } else {
      t20 = $[46];
    }
    let t21;
    if ($[47] !== t14 || $[48] !== t15 || $[49] !== t18 || $[50] !== t19 || $[51] !== t20) {
      t21 = <Dropdown.Group>{t14}{t15}{t18}{t19}{t20}</Dropdown.Group>;
      $[47] = t14;
      $[48] = t15;
      $[49] = t18;
      $[50] = t19;
      $[51] = t20;
      $[52] = t21;
    } else {
      t21 = $[52];
    }
    let t22;
    if ($[53] !== agent || $[54] !== fpId || $[55] !== goToAgentLabel || $[56] !== hasAgentItems || $[57] !== t) {
      t22 = hasAgentItems && <><Dropdown.Divider /><Dropdown.Group>{fpId && <Dropdown.Item iconLeft={IcoMessage16} onSelect={() => setOpenDialog("transcript")} className="h-10">{t(_temp16)}</Dropdown.Item>}{agent && <Dropdown.Item iconLeft={IcoRobot16} asLink={true} onSelect={() => openInNewTab(getAgentPath(agent))} className="h-10">{goToAgentLabel}</Dropdown.Item>}</Dropdown.Group></>;
      $[53] = agent;
      $[54] = fpId;
      $[55] = goToAgentLabel;
      $[56] = hasAgentItems;
      $[57] = t;
      $[58] = t22;
    } else {
      t22 = $[58];
    }
    let t23;
    if ($[59] !== t21 || $[60] !== t22) {
      t23 = <>{t21}{t22}</>;
      $[59] = t21;
      $[60] = t22;
      $[61] = t23;
    } else {
      t23 = $[61];
    }
    const actionItems = t23;
    T2 = Dropdown.Root;
    t10 = false;
    t11 = defaultOpen;
    if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = open => {
        if (open) {
          setView("actions");
        }
      };
      $[62] = t12;
    } else {
      t12 = $[62];
    }
    let t24;
    if ($[63] !== t) {
      t24 = t(_temp17);
      $[63] = t;
      $[64] = t24;
    } else {
      t24 = $[64];
    }
    let t25;
    if ($[65] !== t) {
      t25 = t(_temp18);
      $[65] = t;
      $[66] = t25;
    } else {
      t25 = $[66];
    }
    let t26;
    if ($[67] !== t25) {
      t26 = <IconButton aria-label={t25} icon={IcoDotsHorizontal16} className="size-11 shrink-0" />;
      $[67] = t25;
      $[68] = t26;
    } else {
      t26 = $[68];
    }
    if ($[69] !== t24 || $[70] !== t26) {
      t13 = <Dropdown.Trigger asChild={true} ariaLabel={t24}>{t26}</Dropdown.Trigger>;
      $[69] = t24;
      $[70] = t26;
      $[71] = t13;
    } else {
      t13 = $[71];
    }
    T1 = Dropdown.Portal;
    T0 = Dropdown.Content;
    t7 = "end";
    t8 = "min-w-[220px] max-w-[calc(100vw-16px)]";
    t9 = view === "actions" ? actionItems : <><Dropdown.Item ref={backRef} aria-label={`${tCommon(_temp19)}: ${viewTitle}`} iconLeft={IcoChevronLeft16} onSelect={showView("actions")} className="h-10 font-medium">{viewTitle}</Dropdown.Item><Dropdown.Divider />{view === "fraudLabel" ? <FraudLabelMenuItems /> : <IdMenuItems items={idItems} />}</>;
    $[7] = agent;
    $[8] = canAddFraudLabel;
    $[9] = defaultOpen;
    $[10] = fpId;
    $[11] = goToAgentLabel;
    $[12] = hasAgentItems;
    $[13] = idItems;
    $[14] = onOpenSources;
    $[15] = showSources;
    $[16] = showStatuses;
    $[17] = sourceCount;
    $[18] = subject;
    $[19] = t;
    $[20] = tCommon;
    $[21] = tFraudLabel;
    $[22] = view;
    $[23] = viewTitle;
    $[24] = T0;
    $[25] = T1;
    $[26] = T2;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
  } else {
    T0 = $[24];
    T1 = $[25];
    T2 = $[26];
    t10 = $[27];
    t11 = $[28];
    t12 = $[29];
    t13 = $[30];
    t7 = $[31];
    t8 = $[32];
    t9 = $[33];
  }
  let t14;
  if ($[72] !== T0 || $[73] !== t7 || $[74] !== t8 || $[75] !== t9) {
    t14 = <T0 align={t7} className={t8}>{t9}</T0>;
    $[72] = T0;
    $[73] = t7;
    $[74] = t8;
    $[75] = t9;
    $[76] = t14;
  } else {
    t14 = $[76];
  }
  let t15;
  if ($[77] !== T1 || $[78] !== t14) {
    t15 = <T1>{t14}</T1>;
    $[77] = T1;
    $[78] = t14;
    $[79] = t15;
  } else {
    t15 = $[79];
  }
  let t16;
  if ($[80] !== T2 || $[81] !== t10 || $[82] !== t11 || $[83] !== t12 || $[84] !== t13 || $[85] !== t15) {
    t16 = <T2 modal={t10} defaultOpen={t11} onOpenChange={t12}>{t13}{t15}</T2>;
    $[80] = T2;
    $[81] = t10;
    $[82] = t11;
    $[83] = t12;
    $[84] = t13;
    $[85] = t15;
    $[86] = t16;
  } else {
    t16 = $[86];
  }
  let t17;
  if ($[87] !== customStatuses || $[88] !== entity || $[89] !== openDialog || $[90] !== showStatuses) {
    t17 = showStatuses && <StatusesDialog open={openDialog === "statuses"} onClose={closeDialog} entity={entity} customStatuses={customStatuses} />;
    $[87] = customStatuses;
    $[88] = entity;
    $[89] = openDialog;
    $[90] = showStatuses;
    $[91] = t17;
  } else {
    t17 = $[91];
  }
  let t18;
  if ($[92] !== fpId || $[93] !== investigationId || $[94] !== openDialog) {
    t18 = fpId && <InvestigationChatDialog open={openDialog === "transcript"} onClose={closeDialog} fpId={fpId} investigationId={investigationId} />;
    $[92] = fpId;
    $[93] = investigationId;
    $[94] = openDialog;
    $[95] = t18;
  } else {
    t18 = $[95];
  }
  let t19;
  if ($[96] !== t16 || $[97] !== t17 || $[98] !== t18) {
    t19 = <>{t16}{t17}{t18}</>;
    $[96] = t16;
    $[97] = t17;
    $[98] = t18;
    $[99] = t19;
  } else {
    t19 = $[99];
  }
  return t19;
};
export { ActionButtons, ActionsMenu };
function _temp($) {
  return $.goToBusiness;
}
function _temp2($_0) {
  return $_0.goToUser;
}
function _temp3($) {
  return $.goToAgentVersion;
}
function _temp4($_0) {
  return $_0.goToAgent;
}
function _temp5($) {
  return $.ariaLabel;
}
function _temp6($_0) {
  return $_0.ariaLabel;
}
function _temp7($_1) {
  return $_1.viewTranscript;
}
function _temp8($) {
  return $.sourcesWithCount;
}
function _temp9($_0) {
  return $_0.sourcesAriaLabel;
}
function _temp10($) {
  return $.addLabel;
}
function _temp11($_0) {
  return $_0.copyId;
}
function _temp12($_1) {
  return $_1.statuses;
}
function _temp13($_2) {
  return $_2.addLabel;
}
function _temp14($_3) {
  return $_3.copyId;
}
function _temp15($_4) {
  return $_4.actions.sourcesWithCount;
}
function _temp16($_5) {
  return $_5.agents.viewTranscript;
}
function _temp17($_6) {
  return $_6.moreActions;
}
function _temp18($_7) {
  return $_7.moreActions;
}
function _temp19($_8) {
  return $_8.back;
}